import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useCreateNoteMutation, useUpdateNoteMutation } from "@generated";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { Note } from "../types/Note";
import useStyles from "../styles/useStyles";

interface Props {
  projectId: string;
  note?: Note;
}

export interface NoteFormHandle {
  saveNote: () => void;
}

const NoteForm = forwardRef<NoteFormHandle, Props>(
  ({ projectId, note }, ref) => {
    const { accountSlug } = useSelector((state: RootState) => state.slug);
    const navigate = useNavigate();
    const styles = useStyles();

    const [name, setName] = useState(note?.name || "");
    const [content, setContent] = useState(note?.content || "");

    const [errorMessages, setErrorMessages] = useState("");
    const [showFailAlert, setShowFailAlert] = useState(false);

    const [createNote] = useCreateNoteMutation();
    const [updateNote] = useUpdateNoteMutation();

    const isEdit = !!note;

    useEffect(() => {
      if (note) {
        setName(note.name);
        setContent(note.content);
      }
    }, [note]);

    const buildPayload = () => ({
      data: {
        type: "notes" as const,
        ...(isEdit ? { id: Number(note!.id) } : {}),
        attributes: {
          name,
          content,
        },
      },
    });

    const saveNote = async () => {
      try {
        if (isEdit) {
          await updateNote({
            slug: accountSlug,
            projectId: Number(projectId),
            id: Number(note!.id),
            body: buildPayload() as any,
          }).unwrap();
          navigate(`/projects/${projectId}/notes`, {
            state: { edit: true },
          });
        } else {
          await createNote({
            slug: accountSlug,
            projectId: Number(projectId),
            body: buildPayload() as any,
          }).unwrap();
          navigate(`/projects/${projectId}/notes`, {
            state: { new: true },
          });
        }
      } catch (err: any) {
        const message =
          err?.data?.errors?.[0]?.detail ||
          "Something went wrong. Your changes could not be saved.";
        setErrorMessages(message);
        setShowFailAlert(true);
      }
    };

    useImperativeHandle(ref, () => ({ saveNote }));

    return (
      <>
        {handleAlert(
          showFailAlert,
          errorMessages,
          setShowFailAlert,
          "warning",
          ToastAlert
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className={styles.formLabel}>Note Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className={styles.formLabel}>Note Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </Form.Group>
        </Form>
      </>
    );
  }
);

NoteForm.displayName = "NoteForm";

export default NoteForm;
